import React, { useContext, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Avatar, Chip, Collapse, IconButton, Tooltip, useTheme } from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard'
import CreateIcon from '@mui/icons-material/Create'
import TimelineIcon from '@mui/icons-material/Timeline'
import WatchLaterIcon from '@mui/icons-material/WatchLater'
import PaymentIcon from '@mui/icons-material/Payment'
import TuneIcon from '@mui/icons-material/Tune'
import CodeIcon from '@mui/icons-material/Code'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import LogoutIcon from '@mui/icons-material/Logout'
import VerifiedIcon from '@mui/icons-material/Verified'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import StorageIcon from '@mui/icons-material/Storage'
import KeyIcon from '@mui/icons-material/Key'
import { ApiDataWrapperContext } from '../contexts/ApiDataWrapper'
import { AuthContext } from '../contexts/Auth'
import LinkWithQuery from '../components/LinkWithQuery'
import Text from '../components/Text'
import Logo from '../components/Logo'
import { APP_VERSION } from '../services/appVersion'

const NAV_SECTIONS = [
  {
    label: 'Painel',
    items: [
      { to: '/', icon: SpaceDashboardIcon, label: 'Visão geral' }
    ]
  },
  {
    label: 'Mensagens',
    items: [
      { to: '/messages/send', icon: CreateIcon, label: 'Enviar agora' },
      { to: '/messages/scheduler', icon: WatchLaterIcon, label: 'Expediente de envios' },
      { to: '/reports', icon: TimelineIcon, label: 'Relatórios' }
    ]
  },
  {
    label: 'Negócio',
    items: [
      { to: '/hiperpay/profiles', icon: PaymentIcon, label: 'HiperPay', requires: 'HiperPay' }
    ]
  },
  {
    label: 'Sistema',
    items: [
      { to: '/settings', icon: TuneIcon, label: 'Configurações' },
      { to: '/documentation', icon: CodeIcon, label: 'Documentação', badge: 'NOVIDADES' },
      { to: '/changelog', icon: AccountTreeIcon, label: 'Changelog', badge: `v${APP_VERSION}` }
    ]
  }
]

function formatPhoneBR(raw) {
  if (!raw) return ''
  const digits = String(raw).replace(/\D/g, '')
  // BR celular: 55 + DDD (2) + 9 + 8 digitos
  if (digits.length === 13 && digits.startsWith('55')) {
    return `+55 (${digits.slice(2, 4)}) ${digits.slice(4, 5)} ${digits.slice(5, 9)}-${digits.slice(9)}`
  }
  if (digits.length === 12 && digits.startsWith('55')) {
    return `+55 (${digits.slice(2, 4)}) ${digits.slice(4, 8)}-${digits.slice(8)}`
  }
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 3)} ${digits.slice(3, 7)}-${digits.slice(7)}`
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`
  }
  return raw
}

function getInitials(name, fallback = 'API') {
  if (!name) return fallback
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return fallback
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function CopyableValue({ value, label, icon: Icon }) {
  const theme = useTheme()
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    if (!value) return
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch (e) {
      // ignore
    }
  }

  return (
    <div className="group flex items-center gap-2 py-1.5">
      {Icon && <Icon style={{ fontSize: 14, opacity: 0.55 }} />}
      <div className="flex-1 min-w-0">
        <Text className="text-[10px] font-bold uppercase tracking-wider opacity-50">
          {label}
        </Text>
        <Text
          className="text-xs font-mono truncate"
          style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
        >
          {value || '—'}
        </Text>
      </div>
      {value && (
        <Tooltip title={copied ? 'Copiado!' : 'Copiar'} arrow>
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={{
              opacity: 0.5,
              transition: 'opacity 200ms',
              '&:hover': { opacity: 1, color: theme.palette.primary.main }
            }}
          >
            {copied
              ? <CheckIcon style={{ fontSize: 14, color: theme.palette.primary.main }} />
              : <ContentCopyIcon style={{ fontSize: 14 }} />}
          </IconButton>
        </Tooltip>
      )}
    </div>
  )
}

function NavItem({ to, icon: Icon, label, badge, isActive, onClick }) {
  const theme = useTheme()
  const content = (
    <div
      className="relative flex items-center gap-3 px-3 py-2.5 mx-2 rounded-lg transition-all duration-200 cursor-pointer group"
      style={{
        backgroundColor: isActive ? `${theme.palette.primary.main}1A` : 'transparent',
      }}
      onMouseEnter={e => {
        if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'
      }}
      onMouseLeave={e => {
        if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'
      }}
    >
      {isActive && (
        <span
          className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r"
          style={{ backgroundColor: theme.palette.primary.main }}
        />
      )}
      <Icon
        style={{
          fontSize: 18,
          color: isActive ? theme.palette.primary.main : 'inherit',
          opacity: isActive ? 1 : 0.75,
        }}
      />
      <Text
        className="text-sm flex-1"
        style={{
          fontWeight: isActive ? 700 : 500,
          color: isActive ? theme.palette.primary.main : 'inherit'
        }}
      >
        {label}
      </Text>
      {badge && (
        <Chip
          label={badge}
          size="small"
          sx={{
            height: 18,
            fontSize: 9,
            fontWeight: 800,
            letterSpacing: '0.05em',
            backgroundColor: theme.palette.primary.main,
            color: '#fff'
          }}
        />
      )}
    </div>
  )

  if (onClick) {
    return <div onClick={onClick}>{content}</div>
  }
  return (
    <LinkWithQuery to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
      {content}
    </LinkWithQuery>
  )
}

function SidebarPreview({ onCloseMobile, showMobileClose }) {
  const theme = useTheme()
  const location = useLocation()
  const { logout } = useContext(AuthContext)
  const {
    server, phone, provider, getCredentials, statusSession, user, verifiedName
  } = useContext(ApiDataWrapperContext)
  const { api, secret } = getCredentials()
  const [showSecret, setShowSecret] = useState(false)
  const [credentialsExpanded, setCredentialsExpanded] = useState(false)

  const isAuthenticated = statusSession === 'isLogged'
  const isMeta = provider === 'hiperapi-v3-cloud-official'
  const formattedPhone = formatPhoneBR(phone)
  const displayName = verifiedName || 'Sua conta'
  const initials = getInitials(verifiedName, 'WA')

  const isItemActive = (to) => {
    if (to === '/') return location.pathname === '/'
    return location.pathname.startsWith(to)
  }

  return (
    <div className="min-h-screen w-full flex flex-col preview-card-enter">
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-5 pb-3">
        <div className="flex items-center gap-2">
          <Logo className="w-[120px]" />
          <Chip
            label={`v${APP_VERSION}`}
            size="small"
            sx={{
              height: 18,
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.04em',
              backgroundColor: `${theme.palette.primary.main}1A`,
              color: theme.palette.primary.main
            }}
          />
        </div>
        {showMobileClose && (
          <IconButton size="small" onClick={onCloseMobile}>
            <KeyboardArrowLeftIcon />
          </IconButton>
        )}
      </div>

      {/* Card de conta */}
      <div className="mx-4 mt-2 mb-3 p-4 rounded-2xl" style={{
        backgroundColor: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)'
      }}>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Avatar
              sx={{
                width: 48,
                height: 48,
                backgroundColor: theme.palette.primary.dark,
                color: '#fff',
                fontSize: 14,
                fontWeight: 700
              }}
            >
              {initials}
            </Avatar>
            {isAuthenticated && (
              <span
                className="absolute bottom-0 right-0 w-[12px] h-[12px] rounded-full border-[2px] preview-pulse"
                style={{
                  backgroundColor: theme.palette.primary.main,
                  borderColor: theme.palette.background.paper
                }}
              />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <Text className="text-sm font-bold truncate">{displayName}</Text>
            <Text className="text-xs font-light opacity-70 truncate">
              {formattedPhone || 'Sem número'}
            </Text>
          </div>
        </div>

        <div className="flex items-center gap-1.5 mt-3 flex-wrap">
          {isAuthenticated ? (
            <Chip
              label="Online"
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 700,
                backgroundColor: `${theme.palette.primary.main}1F`,
                color: theme.palette.primary.main
              }}
            />
          ) : statusSession === 'notLogged' ? (
            <Chip
              label="Desconectado"
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 700,
                backgroundColor: 'rgba(239,68,68,0.15)',
                color: '#ef4444'
              }}
            />
          ) : (
            <Chip
              label="Conectando..."
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 700,
                backgroundColor: 'rgba(245,158,11,0.15)',
                color: '#f59e0b'
              }}
            />
          )}
          {isMeta && (
            <Chip
              icon={<VerifiedIcon style={{ fontSize: 12 }} />}
              label="API Oficial"
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 700,
                backgroundColor: 'rgba(52,165,255,0.15)',
                color: '#34a5ff',
                '.MuiChip-icon': { color: '#34a5ff' }
              }}
            />
          )}
          {!isMeta && provider && (
            <Chip
              icon={<WhatsAppIcon style={{ fontSize: 12 }} />}
              label="WhatsApp Web"
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 700,
                backgroundColor: `${theme.palette.primary.main}10`,
                color: theme.palette.primary.main,
                '.MuiChip-icon': { color: theme.palette.primary.main }
              }}
            />
          )}
        </div>

        {/* Credenciais expansíveis */}
        <div
          className="mt-3 pt-3 flex items-center justify-between cursor-pointer select-none"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
          onClick={() => setCredentialsExpanded(v => !v)}
        >
          <Text className="text-xs font-bold uppercase tracking-wider opacity-60">
            Credenciais
          </Text>
          <IconButton size="small" sx={{ p: 0 }}>
            {credentialsExpanded
              ? <ExpandLessIcon style={{ fontSize: 18, opacity: 0.7 }} />
              : <ExpandMoreIcon style={{ fontSize: 18, opacity: 0.7 }} />}
          </IconButton>
        </div>
        <Collapse in={credentialsExpanded}>
          <div className="mt-1 space-y-0.5">
            <CopyableValue label="API" value={api} icon={KeyIcon} />
            <div className="group flex items-center gap-2 py-1.5">
              <KeyIcon style={{ fontSize: 14, opacity: 0.55 }} />
              <div className="flex-1 min-w-0">
                <Text className="text-[10px] font-bold uppercase tracking-wider opacity-50">
                  Secret
                </Text>
                <div className="flex items-center gap-1">
                  <Text
                    className="text-xs font-mono truncate flex-1"
                    style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                  >
                    {secret ? (showSecret ? secret : '•'.repeat(Math.min(secret.length, 16))) : '—'}
                  </Text>
                </div>
              </div>
              <Tooltip title={showSecret ? 'Ocultar' : 'Mostrar'} arrow>
                <IconButton
                  size="small"
                  onClick={() => setShowSecret(v => !v)}
                  sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                >
                  {showSecret
                    ? <VisibilityOffIcon style={{ fontSize: 14 }} />
                    : <VisibilityIcon style={{ fontSize: 14 }} />}
                </IconButton>
              </Tooltip>
            </div>
            <CopyableValue
              label="Servidor"
              value={server ? `Servidor ${server}` : null}
              icon={StorageIcon}
            />
          </div>
        </Collapse>
      </div>

      {/* Navegação */}
      <nav className="flex-1 mt-2 pb-4">
        {NAV_SECTIONS.map((section, sectionIdx) => {
          const visibleItems = section.items.filter(item => {
            if (!item.requires) return true
            return user?.plan?.functionalities?.includes(item.requires)
          })
          if (visibleItems.length === 0) return null
          return (
            <div key={section.label} className={sectionIdx > 0 ? 'mt-4' : ''}>
              <Text className="text-[10px] font-bold uppercase tracking-wider opacity-40 px-5 mb-1">
                {section.label}
              </Text>
              <div className="space-y-0.5">
                {visibleItems.map(item => (
                  <NavItem
                    key={item.to}
                    to={item.to}
                    icon={item.icon}
                    label={item.label}
                    badge={item.badge}
                    isActive={isItemActive(item.to)}
                  />
                ))}
              </div>
            </div>
          )
        })}
      </nav>

      {/* Footer / logout */}
      <div className="mt-auto px-2 pb-4 pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <NavItem
          icon={LogoutIcon}
          label="Sair"
          isActive={false}
          onClick={logout}
        />
        <Text className="text-[10px] font-light opacity-40 text-center mt-3">
          Hipersend Notificações Inteligentes
        </Text>
      </div>
    </div>
  )
}

export default SidebarPreview
